import { useMemo } from 'react';
import { useUIStore, useSettingsStore, useItemStore } from '@/stores';
import { getItemGroupsForCategoryAndSubCategory } from '@perfin/shared';
import type { ItemType } from '@perfin/shared';
import type { PageKey } from '@/stores/uiStore';

interface FilterBarProps {
  itemType: ItemType;
  pageKey: PageKey;
  hideStatusFilter?: boolean;  // Hide status filter (e.g., for recurring items tab)
}

// ============================================================================
// STATUS OPTIONS
// IMPORTANT: Keep in sync with types/index.ts (TaskStatus, TransactionStatus, NoteKeyDateStatus)
// ============================================================================
const getStatusOptions = (itemType: ItemType) => {
  switch (itemType) {
    case 'task':
      // TaskStatus: 'new' | 'inProgress' | 'complete'
      return [
        { value: 'new', label: 'New' },
        { value: 'inProgress', label: 'In Progress' },
        { value: 'complete', label: 'Complete' },
      ];
    case 'transaction':
      // TransactionStatus: 'draft' | 'pending' | 'final'
      return [
        { value: 'draft', label: 'Draft' },
        { value: 'pending', label: 'Pending' },
        { value: 'final', label: 'Final' },
      ];
    case 'note':
    case 'keyDate':
      // NoteKeyDateStatus: 'draft' | 'final'
      return [
        { value: 'draft', label: 'Draft' },
        { value: 'final', label: 'Final' },
      ];
    default:
      return [];
  }
};

const priorityOptions = [
  { value: 'critical', label: 'Critical' },
  { value: 'high', label: 'High' },
  { value: 'medium', label: 'Medium' },
  { value: 'low', label: 'Low' },
  { value: 'future', label: 'Future' },
];

const selectClassName = "rounded-lg border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 min-w-[100px]";
const disabledSelectClassName = "rounded-lg border border-gray-300 px-3 py-1.5 text-sm bg-gray-100 cursor-not-allowed text-gray-400 min-w-[100px]";

export function FilterBar({ itemType, pageKey, hideStatusFilter = false }: FilterBarProps) {
  const {
    categoryFilters,
    setCategoryFilter,
    setSubCategoryFilter,
    pageFilters,
    setOwnerFilter,
    setItemGroupFilter,
    setStatusFilter,
    setPriorityFilter,
  } = useUIStore();
  const { members } = useSettingsStore();
  const getCategoriesForType = useSettingsStore((state) => state.getCategoriesForType);
  const items = useItemStore((state) => state.items);

  const filter = categoryFilters?.[pageKey] ?? { categoryId: null, subCategoryId: null };
  const pFilters = pageFilters?.[pageKey] ?? { ownerFilter: null, itemGroupFilter: null, statusFilter: null, priorityFilter: null };
  const categories = getCategoriesForType(itemType);

  // Get subcategories for selected category
  // When category is "None", there are no subcategories to choose from (but dropdown stays enabled for "None" option)
  const selectedCategory = categories.find((cat) => cat.id === filter.categoryId);
  const subCategories = selectedCategory?.subCategories || [];
  const isCategoryNone = filter.categoryId === 'None';

  // Get available itemGroups for selected category+subCategory
  const availableItemGroups = useMemo(() => {
    if (!filter.categoryId) return [];
    return getItemGroupsForCategoryAndSubCategory(items, filter.categoryId, filter.subCategoryId);
  }, [items, filter.categoryId, filter.subCategoryId]);

  const statusOptions = getStatusOptions(itemType);

  // Compute owner options - all users see all org members
  // NOTE: Filter shows all Owners independent of RBAC - Opportunity to optimize
  // by only showing Owners for the user's accessible items
  const ownerOptions = useMemo(() => {
    return [
      { value: '', label: 'All Owners' },
      ...members.map(m => ({
        value: m.userId,
        label: m.displayName || m.email || m.userId
      }))
    ];
  }, [members]);

  const hasActiveFilters =
    pFilters.ownerFilter !== null ||
    filter.categoryId !== null ||
    filter.subCategoryId !== null ||
    pFilters.itemGroupFilter !== null ||
    pFilters.statusFilter !== null ||
    pFilters.priorityFilter !== null;

  const clearAllFilters = () => {
    setOwnerFilter(pageKey, null);
    setCategoryFilter(pageKey, null);
    setItemGroupFilter(pageKey, null);
    setStatusFilter(pageKey, null);
    setPriorityFilter(pageKey, null);
  };

  return (
    <div className="flex items-center gap-2 flex-wrap">
      {/* Owner dropdown - LEFTMOST */}
      <select
        value={pFilters.ownerFilter || ''}
        onChange={(e) => setOwnerFilter(pageKey, e.target.value || null)}
        className={selectClassName}
      >
        {ownerOptions.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>

      {/* Category dropdown */}
      <select
        value={filter.categoryId || ''}
        onChange={(e) => setCategoryFilter(pageKey, e.target.value || null)}
        className={selectClassName}
      >
        <option value="">All Categories</option>
        <option value="None">None</option>
        {categories.map((category) => (
          <option key={category.id} value={category.id}>
            {category.name}
          </option>
        ))}
      </select>

      {/* Subcategory dropdown - enabled when category is selected (including "None") */}
      <select
        value={filter.subCategoryId || ''}
        onChange={(e) => setSubCategoryFilter(pageKey, e.target.value || null)}
        disabled={!filter.categoryId || (!isCategoryNone && subCategories.length === 0)}
        className={!filter.categoryId || (!isCategoryNone && subCategories.length === 0) ? disabledSelectClassName : selectClassName}
      >
        <option value="">All Subcategories</option>
        <option value="None">None</option>
        {subCategories.map((sub) => (
          <option key={sub.id} value={sub.id}>
            {sub.name}
          </option>
        ))}
      </select>

      {/* ItemGroup dropdown - only enabled when category AND subCategory selected */}
      <select
        value={pFilters.itemGroupFilter || ''}
        onChange={(e) => setItemGroupFilter(pageKey, e.target.value || null)}
        disabled={!filter.categoryId}
        className={!filter.categoryId ? disabledSelectClassName : selectClassName}
      >
        <option value="">All Groups</option>
        <option value="None">None</option>
        {availableItemGroups.map((group) => (
          <option key={group} value={group}>
            {group}
          </option>
        ))}
      </select>

      {/* Priority Filter */}
      <select
        value={pFilters.priorityFilter || ''}
        onChange={(e) => setPriorityFilter(pageKey, e.target.value || null)}
        className={selectClassName}
      >
        <option value="">All Priority</option>
        {priorityOptions.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>

      {/* Status Filter - hidden for recurring items tab */}
      {!hideStatusFilter && (
        <select
          value={pFilters.statusFilter || ''}
          onChange={(e) => setStatusFilter(pageKey, e.target.value || null)}
          className={selectClassName}
        >
          <option value="">All Status</option>
          {statusOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      )}

      {/* Clear button */}
      {hasActiveFilters && (
        <button
          onClick={clearAllFilters}
          className="px-2 py-1 text-xs font-medium text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors"
        >
          Clear
        </button>
      )}
    </div>
  );
}
